import { HTMLAnchorFragment, PDFAnchorFragment } from '@/features/annotation'

import { PaginationInfo, SuccessResponseList } from './common'
import { ResponseDeliverableSet } from './deliverableSet'
import { User } from './user'

export enum ThreadActionType {
  PROGRAM_CHANGE = 'program_change',
  ADAM_CHANGE = 'adam_change',
  SPEC_CHANGE = 'spec_change',
  CLARIFICATION = 'clarification',
}

export enum ThreadActionableType {
  ACTIONABLE = 'actionable',
  NOT_ACTIONABLE = 'not_actionable',
  NONE = 'none',
}

export enum ThreadPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum ThreadSyncStatus {
  NOT_SYNCED = 'not_synced',
  SYNCED = 'synced',
  SYNCING = 'syncing',
  FAILED = 'failed',
}

export interface AnnotationMetadata {
  text: string
  prefix: string
  suffix: string
  fragments: HTMLAnchorFragment[] | PDFAnchorFragment[]
}

/**
 * DTO for creating a new thread (root comment) or reply.

 **Thread Creation (Root Comment):**
 - document_version_id: Required
 - annotation: Required - position data for text selection
 - content: Required
 - mentioned_user_ids: Optional
 - parent_comment_id: Must be NULL
 - quote_comment_id: Must be NULL

 **Reply Creation:**
 - parent_comment_id: Required - must point to root comment (flat threading)
 - quote_comment_id: Optional - contextual reference to specific comment
 - content: Required
 - mentioned_user_ids: Optional
 - document_version_id: Not used (inherited from thread)
 - annotation: Not used (inherited from thread)
 */
export interface RequestCreateThreadOrReply {
  /** Position metadata {page, x, y, width, height, start_offset, end_offset, xpath_selector}. Required for thread creation, not used for replies. */
  annotation?: AnnotationMetadata | null
  /** Plain text comment content (markdown format, max 32000 characters). Required for both threads and replies. */
  content: string
  /** ID of the document version being annotated. Required for thread creation, not used for replies. */
  document_version_id?: string | null
  /** User IDs with metadata (e.g., [{user_id, display_name, avatar_url, profile_url, role}]). Stored as JSONB for flexibility. */
  mentioned_user_ids?: string[] | null
  /** Parent comment ID. Must be NULL for thread creation. Must point to root comment for replies (max depth 2). */
  parent_comment_id?: string | null
  /** Optional contextual reference to specific comment being quoted. Only valid for replies. */
  quote_comment_id?: string | null
}

/**
 * DTO for create comment/thread endpoint response.

 Unified response for both thread creation and reply creation.
 Contains essential fields without thread metadata (status, resolved_at, etc.).
 */
export interface ResponseCreateComment {
  /** Comment content (markdown format) */
  content: string
  /** Creation timestamp */
  created_at: string
  /** Author user ID. None means 'system'. */
  created_by?: string | null
  /** Document version ID */
  document_version_id: string
  /** Comment/Thread ID */
  id: string
  /** User IDs with metadata (e.g., [{user_id, display_name, avatar_url, profile_url, role}]). Stored as JSONB for flexibility. */
  mentioned_user_ids?: string[] | null
  /** Parent comment ID (NULL for threads, root ID for replies) */
  parent_comment_id?: string | null
  /** Quoted comment ID for contextual replies */
  quote_comment_id?: string | null
}

/**
 * DTO for thread (root comment) response.

 Inherits from ResponseCommentDTO and adds thread-specific metadata.
 Used when returning a thread/root comment with its metadata.
 */
export interface ResponseThread {
  /** Type of action required when thread is actionable */
  action_type?: ThreadActionType | null
  /** Whether the thread is marked as actionable, along with timestamp. If not actionable, this will be null. */
  actionable_at?: string | null
  /** User ID who marked the thread as actionable */
  actionable_by?: string | null
  /** Type of actionable marking (none, actionable, non_actionable) */
  actionable_type?: ThreadActionableType | null
  /** Position data {page, x, y, width, height, ...} */
  annotation?: AnnotationMetadata | null
  /** User ID of the assignee when actionable */
  assignee_id?: string | null
  /** Reply content (markdown format) */
  content: string
  /** Last edit timestamp */
  content_last_edited_at?: string | null
  /** Creation timestamp */
  created_at: string
  /** Author user ID. None means 'system'. */
  created_by?: string | null
  /** User details for created_by audit field {id, email, first_name, last_name} */
  created_by_user?: User | null
  /** Deliverable set information */
  deliverable_set?: ResponseDeliverableSet | null
  /** Document ID */
  document_id: string
  /** Document version ID */
  document_version_id: string
  /** Name of the file associated with the thread */
  file_name?: string | null
  /** Whether thread has unread replies for current user */
  has_unread_replies?: boolean
  /** Reply/Comment ID */
  id: string
  /** When current user last read this thread */
  last_read_at?: string | null
  /** Timestamp of the last reply in this thread */
  last_replied_at?: string | null
  /** User IDs with metadata (e.g., [{user_id, display_name, avatar_url, profile_url, role}]). Stored as JSONB for flexibility. */
  mentioned_user_ids?: string[] | null
  /** Parent comment ID (always root or NULL) */
  parent_comment_id?: string | null
  /** Priority level when actionable */
  priority?: ThreadPriority | null
  /** Instructions for the programmer */
  programmer_instructions?: string | null
  /** Quoted comment ID for contextual replies */
  quote_comment_id?: string | null
  /** When thread was resolved */
  resolved_at?: string | null
  /** User who resolved the thread */
  resolved_by?: string | null
  /** External Teamwork resource identifier */
  teamwork_resource_id?: string | null
  /** Type of Teamwork resource (task, comment, etc.) */
  teamwork_resource_type?: string | null
  /** Teamwork sync timestamp */
  teamwork_sync_at?: string | null
  /** Teamwork sync message content */
  teamwork_sync_msg?: string | null
  /** Teamwork sync status */
  teamwork_sync_status?: ThreadSyncStatus | null
  /** Teamwork URL associated with the thread */
  teamwork_sync_url?: string | null
  /** Thread ID */
  thread_id: string
  /** Thread status (open, resolved) */
  thread_status: string
  /** Total number of replies in this thread (excludes root comment) */
  total_replies: number | null
  /** Whether comment was transferred to another document version */
  transferred_at: string | null
  /** Number of unread replies for current user */
  unread_replies: number | null

  /** Version number of the document that the comment is associated with */
  version_number?: number | null

  project?: {
    id: string
    project_name: string
  }
  owner?: User
}

export interface PaginatedResponseResponseThread {
  data: ResponseThread[]
  pagination: PaginationInfo
}

/**
 * DTO for updating comment content and mentions.

 Only the comment creator can edit their comment.
 Updates content and mentioned_user_ids, sets updated_at and updated_by audit fields.
 */
export interface RequestUpdateComment {
  /** Updated plain text comment content (markdown format, max 32000 characters) */
  content: string
  /** User IDs with metadata (e.g., [{user_id, display_name, avatar_url, profile_url, role}]). Stored as JSONB for flexibility. */
  mentioned_user_ids?: string[] | null
}

/**
 * DTO for comment update response.

 Returns the updated comment with audit fields showing who made the update and when.
 */
export interface ResponseUpdateComment {
  /** Updated comment content (markdown format) */
  content: string
  /** Last edit timestamp */
  content_last_edited_at?: string | null
  /** Comment ID */
  id: string
  /** User IDs with metadata (e.g., [{user_id, display_name, avatar_url, profile_url, role}]). Stored as JSONB for flexibility. */
  mentioned_user_ids?: string[] | null
}

/**
 * DTO for updating thread status and annotation.

 Ownership-based constraints:
 - Thread owner: Can change status from open to resolved and vice versa
 - Non-owner: Can only change status from open to resolved (cannot reopen)

 Annotation updates:
 - Optional field to update thread annotation position
 - If provided (not None), updates the annotation metadata
 - If omitted or None, preserves existing annotation
 */
export interface RequestUpdateThread {
  /** Optional annotation position metadata {page, x, y, width, height, start_offset, end_offset, xpath_selector}. If provided, updates the thread annotation. */
  annotation?: { [key: string]: unknown }
  /** Thread status (open, resolved). Required field. */
  status: string
}

/**
 * DTO for comment response

 Contains only essential fields for displaying a comment/reply in a thread.
 */
export interface ResponseComment {
  /** Reply content (markdown format) */
  content: string
  /** Last edit timestamp */
  content_last_edited_at?: string | null
  /** Creation timestamp */
  created_at: string
  /** Author user ID. None means 'system'. */
  created_by?: string | null
  /** User details for created_by audit field {id, email, first_name, last_name} */
  created_by_user?: User | null
  /** Reply/Comment ID */
  id: string
  /** User IDs with metadata (e.g., [{user_id, display_name, avatar_url, profile_url, role}]). Stored as JSONB for flexibility. */
  mentioned_user_ids?: string[] | null
  /** Parent comment ID (always root or NULL) */
  parent_comment_id?: string | null
  /** Quoted comment ID for contextual replies */
  quote_comment_id?: string | null
  project?: {
    id: string
    project_name: string
  }
}

/**
 * DTO for marking a thread as actionable.
 All other fields are optional and used when marking as actionable.
 */
export interface RequestMarkThreadActionable {
  /** Type of action required. Used when marking thread as actionable. */
  action_type?: ThreadActionType | null
  /** Type of actionable marking */
  actionable_type?: ThreadActionableType | null
  /** User ID of the assignee (optional) */
  assignee_id?: string | null
  /** Whether to send this actionable thread to Teamwork integration */
  is_sent_to_teamwork?: boolean | null
  /** Priority level (optional, defaults to medium) */
  priority?: ThreadPriority | null
  /** Instructions for the programmer (max 255 characters) */
  programmer_instructions?: string | null
}

export interface SuccessResponseListResponseThread extends SuccessResponseList<ResponseThread> {}

export interface SuccessResponseListResponseComment extends SuccessResponseList<ResponseComment> {}
